/**
 * Order Linked Receipts Component
 *
 * Displays a list of goods receipts linked to a purchase order.
 * @see US-9.6 - Action "Recevoir" sur Commande
 */

import { FileText, Plus, ExternalLink } from 'lucide-react';
import { Badge, Button, Skeleton } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useGoodsReceipts } from '@/features/goods-receipts';
import {
  GoodsReceiptStatus,
  GOODS_RECEIPT_STATUS_LABELS,
  GOODS_RECEIPT_STATUS_COLORS,
} from '@bstock/shared';
import { useNavigate } from 'react-router-dom';

interface OrderLinkedReceiptsProps {
  /** Purchase order ID */
  orderId: string;
  /** Whether to show the create button (order must be SENT or PARTIALLY_RECEIVED) */
  showCreateButton: boolean;
  /** CSS class name */
  className?: string;
  /** Show financial data (prices, totals) */
  showFinancials?: boolean;
}

/**
 * Map status to badge variant
 */
function getStatusVariant(status: GoodsReceiptStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    green: 'success',
    gray: 'neutral',
    red: 'danger',
  };
  return colorMap[GOODS_RECEIPT_STATUS_COLORS[status]] ?? 'neutral';
}

export function OrderLinkedReceipts({
  orderId,
  showCreateButton,
  className,
  showFinancials = false,
}: OrderLinkedReceiptsProps) {
  const navigate = useNavigate();
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();

  const { data: receiptsData, isLoading } = useGoodsReceipts({
    purchaseOrderId: orderId,
    limit: 50,
    sortBy: 'receiptDate',
    sortOrder: 'desc',
  });

  const receipts = receiptsData?.data ?? [];

  const handleCreateReceipt = () => {
    navigate(`/achats/receptions/create?fromOrder=true&orderId=${orderId}`);
  };

  const handleViewReceipt = (receiptId: string) => {
    navigate(`/achats/receptions/${receiptId}`);
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-neutral-700 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Receptions liees
        </h3>
        {showCreateButton && (
          <Button variant="secondary" size="sm" onClick={handleCreateReceipt}>
            <Plus className="w-4 h-4" />
            Recevoir
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : receipts.length === 0 ? (
        <div className="bg-neutral-50 rounded-lg p-6 text-center">
          <FileText className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-sm text-neutral-500 mb-4">
            Aucune reception enregistree pour cette commande.
          </p>
          {showCreateButton && (
            <Button variant="primary" size="sm" onClick={handleCreateReceipt}>
              <Plus className="w-4 h-4" />
              Creer une reception
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-neutral-700">Date</th>
                <th className="text-left px-4 py-2 font-medium text-neutral-700">N. BR</th>
                {showFinancials && (
                  <th className="text-right px-4 py-2 font-medium text-neutral-700">Montant</th>
                )}
                <th className="text-center px-4 py-2 font-medium text-neutral-700">Statut</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {receipts.map((receipt) => (
                <tr key={receipt.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 text-neutral-600">
                    {formatDateShort(receipt.receiptDate)}
                  </td>
                  <td className="px-4 py-3 font-medium text-dark-500">
                    {receipt.receiptNumber}
                  </td>
                  {showFinancials && (
                    <td className="px-4 py-3 text-right text-neutral-900">
                      {formatCentimes(receipt.totalExclTax, currency)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <Badge variant={getStatusVariant(receipt.status)}>
                        {GOODS_RECEIPT_STATUS_LABELS[receipt.status]}
                      </Badge>
                      {!receipt.isDefinitive && (
                        <Badge variant="warning">Provisoire</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <button
                      onClick={() => handleViewReceipt(receipt.id)}
                      className="p-1.5 rounded-lg text-neutral-400 hover:text-primary-500 hover:bg-primary-50 transition-colors"
                      aria-label="Voir la reception"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
